import { useState } from "react";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  ArchiveRestore,
  CheckCircle,
  AlertTriangle,
  Info,
  Shield,
  RotateCcw,
  Database,
} from "lucide-react";
import { ScanAPI, Scan } from "@/api/scans";

interface RestoreScanProps {
  isOpen: boolean;
  onClose: () => void;
  scan: Scan | null;
  onSuccess?: () => void;
}

export function RestoreScan({ isOpen, onClose, scan, onSuccess }: RestoreScanProps) {
  const [isRestoring, setIsRestoring] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRestore = async () => {
    if (!scan) return;

    setIsRestoring(true);
    setError(null);

    try {
      const response = await ScanAPI.restore(scan.id);

      if (response.code === '200') {
        setIsRestoring(false);
        setIsComplete(true);

        if (onSuccess) {
          onSuccess();
        }

        onClose();
          setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to restore scan');
      }
    } catch (error) {
      setIsRestoring(false);
      setError(error instanceof Error ? error.message : 'Failed to restore scan. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Scan Restored Successfully!</h3>
          <p className="text-muted-foreground">
            The scan "{scan?.id}" has been restored and is now active.
          </p>
        </div>
      );
    }

    if (isRestoring) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Restoring Scan...</h3>
          <p className="text-muted-foreground">Moving scan back to active state...</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
       
          <div className="flex-1">

            <p className="text-muted-foreground">
              Are you sure you want to restore the scan <strong>{scan?.code}</strong>?
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <ArchiveRestore className="w-5 h-5 text-blue-600" />
            <span>Restore Scan</span>
          </DialogTitle>
          <DialogDescription>
            This action will restore the scan and make it active again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="mt-4">
            {renderContent()}
          </div>

          {!isRestoring && !isComplete && (
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRestore}
                className="bg-blue-600 hover:bg-blue-700 rounded-lg"
              >
                <ArchiveRestore className="w-4 h-4 mr-2" />
                Restore Scan
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}